import { create } from "zustand"

import Toast from "@/components/Toast"
import { requestGetRole, requestLogin, requestLoginByAuth, requestLogout } from "@/services/user"
import { getWebViewAuth, getWebViewChannel } from "@/utils/sdk"
import { webSdkLogin, webSdkLogout } from "@/utils/websdk"

export type RoleInfo = {
    uid: string
    nickName: string
    channelId: number
}

type SessionState = {
    user: RoleInfo | null
    isLoggingIn: boolean
    sessionReady: boolean
    setUser: (user: RoleInfo | null) => void
    setLoggingIn: (loading: boolean) => void
    setSessionReady: (ready: boolean) => void
}

const useSessionStore = create<SessionState>((set) => ({
    user: null,
    isLoggingIn: false,
    sessionReady: false,
    setUser: (user) => set({ user }),
    setLoggingIn: (isLoggingIn) => set({ isLoggingIn }),
    setSessionReady: (sessionReady) => set({ sessionReady }),
}))

const SESSION_FLAG_KEY = "ak_session_active"
const JWT_TOKEN_KEY = "ak_jwt_token"

/**
 * 通过 WebSDK 登录，获取 token 后调用后端接口绑定角色
 */
export const login = async (): Promise<RoleInfo | null> => {
    const { isLoggingIn, setLoggingIn, setUser } = useSessionStore.getState()

    if (isLoggingIn) {
        return null
    }

    setLoggingIn(true)
    try {
        // 1. 调用 WebSDK 登录，获取 LOGIN_UID 和 LOGIN_TOKEN
        const sdkResult = await webSdkLogin()
        if (!sdkResult) {
            return null
        }

        // 2. 用 token 和 uid 调用后端登录接口
        const channel = getWebViewChannel() || "official"
        const loginRes = await requestLogin(sdkResult.loginToken, sdkResult.loginUid, channel)
        if (loginRes.code !== 0) {
            Toast.message(loginRes.msg || "Login failed")
            await webSdkLogout()
            return null
        }

        // 3. 从登录响应中提取用户信息，持久化 JWT token
        const results = loginRes.data?.results
        const userData: RoleInfo = {
            uid: results?.userId || "",
            nickName: results?.nickname || "",
            channelId: 1,
        }

        setUser(userData)
        localStorage.setItem(SESSION_FLAG_KEY, "1")
        if (results?.token) {
            localStorage.setItem(JWT_TOKEN_KEY, results.token)
        }
        return userData
    } catch (err) {
        console.error("Login error:", err)
        Toast.message("Login error, please try again")
        return null
    } finally {
        setLoggingIn(false)
    }
}

/**
 * 登出：先调用后端登出，再 WebSDK 登出，最后清除本地状态
 */
export const logout = async () => {
    try {
        await requestLogout()
    } catch (err) {
        console.error("Server logout error:", err)
    }
    try {
        await webSdkLogout()
    } catch (err) {
        console.error("SDK logout error:", err)
    }
    localStorage.removeItem(SESSION_FLAG_KEY)
    localStorage.removeItem(JWT_TOKEN_KEY)
    useSessionStore.getState().setUser(null)
}

const applyAuthResult = (res: Awaited<ReturnType<typeof requestLoginByAuth>>): RoleInfo | null => {
    if (res?.code !== 0 || !res.data?.results) return null
    const results = res.data.results
    const userData: RoleInfo = {
        uid: results.userId || "",
        nickName: results.nickname || "",
        channelId: 1,
    }
    useSessionStore.getState().setUser(userData)
    if (results.token) {
        localStorage.setItem(JWT_TOKEN_KEY, results.token)
        localStorage.setItem(SESSION_FLAG_KEY, "1")
    }
    return userData
}

/**
 * 启动时尝试恢复登录状态，按优先级：
 * 1. WebView 认证（URL auth 参数，游戏内 WebView）
 * 2. JWT 续期（localStorage 存储的 token）
 * 3. GET 验证 Cookie（仅当有历史登录记录时尝试，避免未登录用户触发 403）
 */
export const restoreSession = async (): Promise<RoleInfo | null> => {
    // 1. WebView 认证（URL auth 参数）
    const webViewAuth = getWebViewAuth()
    if (webViewAuth) {
        try {
            const res = await requestLoginByAuth(webViewAuth)
            const userData = applyAuthResult(res)
            if (userData) return userData
            console.warn("WebView auth failed (code:", res?.code, ")")
        } catch (err) {
            console.warn("WebView auth error:", err)
        }
        return null
    }

    // 2. JWT 续期
    const storedToken = localStorage.getItem(JWT_TOKEN_KEY)
    if (storedToken) {
        try {
            const res = await requestLoginByAuth(storedToken)
            const userData = applyAuthResult(res)
            if (userData) return userData
            console.warn("JWT renewal failed (code:", res?.code, "), clearing session")
            localStorage.removeItem(SESSION_FLAG_KEY)
            localStorage.removeItem(JWT_TOKEN_KEY)
            useSessionStore.getState().setUser(null)
        } catch (err) {
            console.warn("JWT renewal error:", err)
            localStorage.removeItem(SESSION_FLAG_KEY)
            localStorage.removeItem(JWT_TOKEN_KEY)
            useSessionStore.getState().setUser(null)
        }
        return null
    }

    // 3. GET 验证 Cookie（仅当有历史登录标记时才发请求，避免未登录用户触发 403）
    if (localStorage.getItem(SESSION_FLAG_KEY)) {
        try {
            const res = await requestGetRole()
            if (res?.code === 0) {
                return applyAuthResult(res)
            }
            // Session expired or invalid — clear flag to avoid repeated 403 on next load
            localStorage.removeItem(SESSION_FLAG_KEY)
        } catch (err) {
            console.warn("Cookie check failed:", err)
            localStorage.removeItem(SESSION_FLAG_KEY)
        }
    }

    return null
}

// 应用加载时自动尝试恢复会话
export const sessionReadyPromise = restoreSession().finally(() => {
    useSessionStore.getState().setSessionReady(true)
})

// ---- Hooks ----

export const useRoleInfo = () => useSessionStore((state) => state.user)

export const useLogin = () => login

export const useLogout = () => logout

export const useIsLoggingIn = () =>
    useSessionStore((state) => state.isLoggingIn)

export const useSessionReady = () =>
    useSessionStore((state) => state.sessionReady)

/**
 * 确保用户已登录，如果未登录则触发登录流程
 */
export const ensureLogin = () => {
    const { user } = useSessionStore.getState()
    if (!user) {
        login()
    }
    return !!user
}

/**
 * DEV ONLY: 跳过 SDK 登录，直接用假用户进入页面
 */
export const devBypassLogin = () => {
    const userData: RoleInfo = {
        uid: "dev#0000",
        nickName: "DEV_USER",
        channelId: 1,
    }
    useSessionStore.getState().setUser(userData)
    useSessionStore.getState().setSessionReady(true)
    localStorage.setItem(SESSION_FLAG_KEY, "1")
    return userData
}

export const useRoleMeta = () => {
    const user = useRoleInfo()
    return user
        ? {
              channel: "official",
              uid: user.uid,
              nickName: user.nickName,
          }
        : null
}
